import React from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import LandingPage from "../pages/landingPage/LandingPage.jsx";
import Requests from "../pages/requests/Requests.jsx";
import Payment from "../pages/payments/Payment.jsx";
import PersonalInfo from "../pages/userPersonalInfo/PersonalInfo.jsx";

import DashboardMain from "../pages/managerDashboard/DashboardMain.jsx";
import PayrollDetails from "../pages/payrollDetails/PayrollDetails.jsx";
import AuditDetails from "../pages/auditDetails/AuditDetails.jsx";
import AdditionalPayments from "../pages/additionalPayments/AdditionalPayments.jsx";

import UserSummary from "../pages/userSummaryManagerView/UserSummary.jsx";
import UserPersonalInfo from "../pages/personalInfoManagerView/UserPersonalInfo.jsx";
import UserDailyDiary from "../pages/userDiaryManagerView/UserDailyDiary.jsx";
import UserRequest from "../pages/userRequestsManagerView/UserRequest.jsx";
import UserCompliances from "../pages/userCompliances/UserCompliances.jsx";

import AdminSettings from "../pages/adminSettings/AdminSettings.jsx";
import AdminAnnouncementsPage from "../pages/announcements/AdminAnnouncementsPage.jsx";
import AdminWebTrackerPage from "../pages/webTracker/AdminWebTrackerPage.jsx";
import AdminComplianceSettingsPage from "../pages/complianceSettings/AdminComplianceSettingsPage.jsx";
import TrolleySetup from "../pages/trolleySetup/TrolleySetup.jsx";
import AdminAdditionalPaymentsPage from "../pages/adminAdditionalPayments/AdminAdditionalPaymentsPage.jsx";
import AdminProjectSettingsPage from "../pages/projectSettings/AdminProjectSettingsPage.jsx";

import WfmIframe from "../pages/workforce/WfmIframe.jsx";

import ApplicantRestrictedPage from "../pages/applicantRestrictedPage/ApplicantRestrictedPage.jsx";
import SignIn from "../pages/signin/SignIn.jsx";
import ApplicantSignIn from "../pages/applicantSignin/ApplicantSignIn.jsx";
import ApplicantRegister from "../pages/applicantRegister/applicantRegister.jsx";

import Summary from "../pages/summary/Summary.jsx";
import Diary from "../pages/diary/Diary.jsx";
import Links from "../pages/links/Links.jsx";

import ManagerProjects from "../pages/managerProjects/ManagerProjects.jsx";
import ManualTimeApproval from "../pages/manualTimeApproval/ManualTimeApproval.jsx";

import UpdateUser from "../pages/updateUser/UpdateUser.jsx";
import JobRequest from "../pages/jobRequest/jobRequest.jsx";

import OpenJobs from "../pages/openJobs/OpenJobs.jsx";
import ApplicantManageJobs from "../pages/applicantManageJobs/ApplicantManageJobs.jsx";
import ManageJobs from "../pages/manageJobs/ManageJobs.jsx";
import Job from "../pages/job/Job.jsx";

import RolePermissions from "../pages/rolePermissions/RolePermissions.jsx";

import AuthenticatedRoute from "./protectedRoute/AuthenticatedRoute.jsx";
import PermissionRoute from "./protectedRoute/PermissionRoute.jsx";
import RoleRoute from "./protectedRoute/RoleRoute.jsx";
import TeamMemberProtectedRoute from "./protectedRoute/TeamMemberProtectedRoute.jsx";

import { RoleLayout } from "./RoleLayout.jsx";

import { PERMISSIONS } from "../security";
import Coaching from "../pages/coaching/Coaching.jsx";
import UserCoachingsPage from "../components/userCoachings/UserCoachingsPage.jsx";
import MyGrowth from "../pages/myGrowth/MyGrowth.jsx";
import AnonymousFeedback from "../pages/anonymousFeedback/AnonymousFeedback.jsx";
import UserBlockers from "../pages/userBlockers/UserBlockers.jsx";

/**
 * Wraps a route element with permission validation.
 *
 * @param {object} permission
 * @param {JSX.Element} element
 * @returns {JSX.Element}
 */
const permitted = (permission, element) => (
  <PermissionRoute permission={permission}>
    {element}
  </PermissionRoute>
);

/**
 * Application routes.
 *
 * Route protection strategy:
 *
 * 1. Public routes
 *    - No authentication required.
 *
 * 2. Personal/authenticated routes
 *    - Authentication is required.
 *    - Access is primarily controlled by RoleLayout / RoleRoute.
 *    - These routes do not require API grants.
 *
 * 3. Manager/admin routes
 *    - Authentication is required.
 *    - PermissionRoute validates API grants.
 *
 * 4. Team member routes
 *    - Authentication is required.
 *    - TeamMemberProtectedRoute validates team relationship.
 *    - PermissionRoute validates API grants.
 *
 * @returns {JSX.Element}
 */
export function Router() {
  return (
    <Routes>
      {/* ========================================================= */}
      {/* PUBLIC ROUTES */}
      {/* ========================================================= */}

      <Route
        path="/signin"
        element={<SignIn />}
      />

      <Route
        path="/applicant-signin"
        element={<ApplicantSignIn />}
      />

      <Route
        path="/applicant/sign-up"
        element={<ApplicantRegister />}
      />

      <Route
        path="/open-jobs"
        element={<OpenJobs />}
      />

      <Route
        path="/open-jobs/job/:uuid"
        element={<Job />}
      />

      <Route
        path="/"
        element={<LandingPage />}
      />

      {/* ========================================================= */}
      {/* AUTHENTICATED ROUTES */}
      {/* ========================================================= */}

      <Route element={<AuthenticatedRoute />}>
        <Route element={<RoleLayout />}>

          {/* ===================================================== */}
          {/* WORKFORCE */}
          {/* ===================================================== */}

          <Route
            path="/workforce"
            element={
              <RoleRoute roles={["CEO", "VP", "HR", "Manager", "Team Leader", "Agent", "CSM"]}>
                <WfmIframe />
              </RoleRoute>
            }
          />

          {/* ===================================================== */}
          {/* PERSONAL ROUTES */}
          {/* ===================================================== */}
          {/*
            These routes belong to the authenticated user's
            personal area.

            They should NOT depend on API grants because the user
            must be able to access their own dashboard/profile.

            Authentication and RoleLayout are sufficient here.
          */}

          <Route
            path="/summary"
            element={<Summary />}
          />

          <Route
            path="/diary"
            element={<Diary />}
          />

          <Route
            path="/profile/info"
            element={<PersonalInfo />}
          />

          <Route
            path="/profile/requests"
            element={<Requests />}
          />

          <Route
            path="/profile/growth"
            element={<MyGrowth />}
          />

          <Route
            path="/profile/payment"
            element={<Payment />}
          />

          <Route
            path="/profile/links"
            element={<Links />}
          />

          {/* ===================================================== */}
          {/* JOB ROUTES */}
          {/* ===================================================== */}

          <Route
            path="/manage-jobs"
            element={permitted(
              PERMISSIONS.jobs_get,
              <ManageJobs />
            )}
          />

          <Route
            path="/applicant/manage-jobs"
            element={permitted(
              PERMISSIONS.jobApplicationsApplications_get,
              <ApplicantManageJobs />
            )}
          />

          <Route
            path="/job/:uuid"
            element={permitted(
              PERMISSIONS.jobsByUuid_get,
              <Job />
            )}
          />

          {/* ===================================================== */}
          {/* MANAGER ROUTES */}
          {/* ===================================================== */}

          <Route
            path="/manager"
            element={permitted(
              PERMISSIONS.managers_get,
              <DashboardMain />
            )}
          />

          <Route
            path="/manager/job-request"
            element={permitted(
              PERMISSIONS.jobs_post,
              <JobRequest />
            )}
          />

          <Route
            path="/manager/payroll-details"
            element={permitted(
              PERMISSIONS.reportsPayroll_get,
              <PayrollDetails />
            )}
          />

          <Route
            path="/manager/additional-payments"
            element={permitted(
              PERMISSIONS.additionalPayments_get,
              <AdditionalPayments />
            )}
          />

          <Route
            path="/manager/growth-management"
            element={permitted(
              PERMISSIONS.reportsPayroll_get, 
              <Coaching />
            )}
          />

          <Route
            path="/manager/projects"
            element={permitted(
              PERMISSIONS.projects_get,
              <ManagerProjects />
            )}
          />

          <Route
            path="/manager/manual-time-approval"
            element={permitted(
              PERMISSIONS.requestsManualTime_get,
              <ManualTimeApproval />
            )}
          />

          <Route 
              path="/manager/anonymous-feedback"
              element={permitted(
                PERMISSIONS.complianceUsersById_get,
                <AnonymousFeedback />
              )}
            />

          {/* ===================================================== */}
          {/* MANAGER - TEAM MEMBER ROUTES */}
          {/* ===================================================== */}

          <Route element={<TeamMemberProtectedRoute />}>

            <Route
              path="/manager/summary/:employeeId"
              element={permitted(
                PERMISSIONS.reportsUsersByUseridSummary_get,
                <UserSummary />
              )}
            />

            <Route
              path="/manager/personalInfo/:employeeId"
              element={permitted(
                PERMISSIONS.usersById_get,
                <UserPersonalInfo />
              )}
            />

            <Route
              path="/manager/coaching/:employeeId"
              element={permitted(
                PERMISSIONS.usersById_get,
                <UserCoachingsPage />
              )}
            />

            <Route
              path="/manager/diary/:employeeId"
              element={permitted(
                PERMISSIONS.trackingUsersByIdDiary_get,
                <UserDailyDiary />
              )}
            />

            <Route
              path="/manager/user-requests/:employeeId"
              element={permitted(
                PERMISSIONS.requestsUsersByUseridAll_get,
                <UserRequest />
              )}
            />

            <Route
              path="/manager/compliance/:employeeId"
              element={permitted(
                PERMISSIONS.complianceUsersById_get,
                <UserCompliances />
              )}
            />

            <Route
              path="/manager/blockers/:employeeId"
              element={permitted(
                PERMISSIONS.complianceUsersById_get,
                <UserBlockers />
              )}
            />

          </Route>

          {/* ===================================================== */}
          {/* SYSTEM SETTINGS */}
          {/* ===================================================== */}

          <Route
            path="/settings/sso-setup"
            element={permitted(
              PERMISSIONS.settings_get,
              <AdminSettings />
            )}
          />

          <Route
            path="/settings/announcements"
            element={permitted(
              PERMISSIONS.announcementsAll_get,
              <AdminAnnouncementsPage />
            )}
          />

          <Route
            path="/settings/web-tracker"
            element={permitted(
              PERMISSIONS.desktopAppGetusersstatus_get,
              <AdminWebTrackerPage />
            )}
          />

          <Route
            path="/settings/compliance-settings"
            element={permitted(
              PERMISSIONS.complianceSettings_get,
              <AdminComplianceSettingsPage />
            )}
          />

          <Route
            path="/settings/audit-logs"
            element={permitted(
              PERMISSIONS.audit_get,
              <AuditDetails />
            )}
          />

          <Route
            path="/settings/project-settings"
            element={permitted(
              PERMISSIONS.projects_get,
              <AdminProjectSettingsPage />
            )}
          />

          {/* ===================================================== */}
          {/* USER SETTINGS */}
          {/* ===================================================== */}

          <Route
            path="/user-settings/trolley-report"
            element={permitted(
              PERMISSIONS.reportsTrolleyPayroll_get,
              <TrolleySetup />
            )}
          />

          <Route
            path="/user-settings/update-user"
            element={permitted(
              PERMISSIONS.users_get,
              <UpdateUser />
            )}
          />

          <Route
            path="/user-settings/additional-payments"
            element={permitted(
              PERMISSIONS.additionalPayments_get,
              <AdminAdditionalPaymentsPage />
            )}
          />

          <Route
            path="/user-settings/role-permissions"
            element={permitted(
              PERMISSIONS.rolePermissions_get,
              <RolePermissions />
            )}
          />

          {/* ===================================================== */}
          {/* APPLICANT */}
          {/* ===================================================== */}

          <Route
            path="/applicant/restricted"
            element={<ApplicantRestrictedPage />}
          />

        </Route>
      </Route>

      <Route
        path="/unauthorized"
        element={
          <p>
            You do not have permission to access this page.
          </p>
        }
      />

      <Route
        path="/permission-error"
        element={
          <p>
            Permissions could not be loaded. Please try again.
          </p>
        }
      />

      <Route
        path="*"
        element={
          <Navigate
            to="/"
            replace
          />
        }
      />
    </Routes>
  );
}